import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Flame, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { LinkedInIcon } from '@/components/icons';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { Button, Container } from '@/components/ui';
import { useAuth } from '@/features/auth/useAuth';
import { api } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  href: string;
}

/**
 * In-page anchors rather than routes: these are sections of the landing page, so
 * a plain `<a href="/#features">` is both simpler and correct — it works from any
 * route and needs no scroll orchestration beyond `ScrollRestoration`.
 */
const GUEST_NAV_ITEMS: NavItem[] = [
  { label: 'Features', href: '/#features' },
  { label: 'How it works', href: '/#how-it-works' },
  { label: 'Companies', href: '/companies' },
  { label: 'Leaderboard', href: '/leaderboard' },
  { label: 'FAQ', href: '/#faq' },
];

const AUTH_NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Resume', href: '/resume' },
  { label: 'Interviews', href: '/interviews' },
  { label: 'Coding', href: '/coding' },
  { label: 'Mentor', href: '/mentor' },
  { label: 'Roadmap', href: '/roadmap' },
  { label: 'Companies', href: '/companies' },
  { label: 'Leaderboard', href: '/leaderboard' },
];

const linkClass =
  'rounded-lg px-2.5 py-2 text-sm whitespace-nowrap transition-colors hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]';

export function Navbar() {
  const { isAuthenticated, isReady } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api.me.get(signal),
    enabled: isReady && isAuthenticated,
    staleTime: 60_000,
  });
  const showAdmin = meQuery.data?.profile.is_admin === true;

  const primaryNav: NavItem[] =
    isReady && isAuthenticated
      ? showAdmin
        ? [...AUTH_NAV_ITEMS, { label: 'Admin', href: '/admin' }]
        : AUTH_NAV_ITEMS
      : GUEST_NAV_ITEMS;

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMenuOpen]);

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-all duration-300 ease-[var(--ease-smooth)]',
        isScrolled ? 'glass border-b py-2' : 'border-b border-transparent py-3',
      )}
    >
      <Container className="flex items-center justify-between gap-3">
        <Link
          to="/"
          onClick={closeMenu}
          className="group flex shrink-0 items-center gap-2.5 rounded-lg"
          aria-label="GoInterview — home"
        >
          <span className="from-brand-500 to-accent-500 shadow-brand-500/30 grid size-9 place-items-center rounded-xl bg-gradient-to-br shadow-lg transition-transform duration-300 group-hover:scale-105">
            <Flame className="size-5 text-white" aria-hidden="true" />
          </span>
          <span className="text-[15px] font-semibold tracking-tight">
            Go<span className="text-brand-500 dark:text-brand-400">Interview</span>
          </span>
        </Link>

        <nav
          aria-label="Main"
          className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 lg:flex"
        >
          {primaryNav.map((item) =>
            item.href.startsWith('/#') || item.href.startsWith('#') ? (
              <a
                key={item.href}
                href={item.href}
                className={cn(linkClass, 'text-[var(--text-secondary)]')}
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    linkClass,
                    isActive
                      ? 'bg-[var(--surface-sunken)] text-[var(--text-primary)]'
                      : 'text-[var(--text-secondary)]',
                  )
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          <ThemeToggle className="hidden sm:inline-flex" />
          {showAdmin ? (
            <NavLink to="/status" className="hidden sm:block">
              {({ isActive }) => (
                <Button size="sm" variant={isActive ? 'secondary' : 'ghost'}>
                  Status
                </Button>
              )}
            </NavLink>
          ) : null}

          {isReady && isAuthenticated ? (
            <NavLink to="/account" className="hidden sm:block">
              {({ isActive }) => (
                <Button size="sm" variant={isActive ? 'secondary' : 'primary'}>
                  Account
                </Button>
              )}
            </NavLink>
          ) : isReady ? (
            <>
              <NavLink to="/sign-in" className="hidden sm:block">
                <Button size="sm" variant="ghost">
                  Sign in
                </Button>
              </NavLink>
              <NavLink to="/sign-up" className="hidden sm:block">
                <Button size="sm">Get started</Button>
              </NavLink>
            </>
          ) : null}

          <button
            type="button"
            className="grid size-9 place-items-center rounded-lg text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-sunken)] lg:hidden"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => {
              setIsMenuOpen((open) => !open);
            }}
          >
            {isMenuOpen ? (
              <X className="size-5" aria-hidden="true" />
            ) : (
              <Menu className="size-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </Container>

      <AnimatePresence>
        {isMenuOpen ? (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="glass mt-2 border-t lg:hidden"
          >
            <Container className="flex flex-col gap-1 py-4">
              {primaryNav.map((item) =>
                item.href.startsWith('/#') || item.href.startsWith('#') ? (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={closeMenu}
                    className="rounded-lg px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={closeMenu}
                    className="rounded-lg px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
                  >
                    {item.label}
                  </NavLink>
                ),
              )}
              {showAdmin ? (
                <NavLink
                  to="/status"
                  onClick={closeMenu}
                  className="rounded-lg px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
                >
                  System status
                </NavLink>
              ) : null}
              {isAuthenticated ? (
                <NavLink
                  to="/account"
                  onClick={closeMenu}
                  className="rounded-lg px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
                >
                  Account
                </NavLink>
              ) : (
                <>
                  <NavLink
                    to="/sign-in"
                    onClick={closeMenu}
                    className="rounded-lg px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
                  >
                    Sign in
                  </NavLink>
                  <NavLink
                    to="/sign-up"
                    onClick={closeMenu}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-brand-600 hover:bg-[var(--surface-sunken)] dark:text-brand-400"
                  >
                    Get started
                  </NavLink>
                </>
              )}
              <div className="mt-3 flex items-center justify-between gap-3 border-t pt-4">
                <ThemeToggle />
                <a
                  href="https://www.linkedin.com/in/adityakqumar/"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex-1"
                >
                  <Button size="sm" fullWidth leftIcon={<LinkedInIcon className="size-4" />}>
                    LinkedIn
                  </Button>
                </a>
              </div>
            </Container>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
